import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowsClockwise, Trash, ChatCircleDots } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_LABEL = {
  product: { label: 'Produkt', color: 'bg-blue-100 text-blue-800' },
  category: { label: 'Kategori', color: 'bg-purple-100 text-purple-800' },
  term: { label: 'Attributt', color: 'bg-amber-100 text-amber-800' },
};

/**
 * RejectedQueue — list of proposals the user clicked "Avvis" on.
 * Auto-cleared once a NEW approval for the same product is approved+pushed.
 * Each row offers Re-prøv (re-queue with priority) and Slett permanent.
 */
export default function RejectedQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const getHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    [],
  );

  const fetchRejected = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/autoguard/rejected`, { headers: getHeaders() });
      setItems(data);
    } catch (e) {
      // Silent — endpoint may not exist on older backend
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { fetchRejected(); }, [fetchRejected]);

  const retry = async (id) => {
    try {
      await axios.post(`${API}/api/autoguard/rejected/${id}/retry`, {}, { headers: getHeaders() });
      toast.success('Lagt tilbake i AutoGuard-køen med prioritet');
      fetchRejected();
    } catch {
      toast.error('Kunne ikke re-prøve');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Slett dette avviste forslaget permanent?')) return;
    try {
      await axios.delete(`${API}/api/autoguard/rejected/${id}`, { headers: getHeaders() });
      toast.success('Slettet');
      fetchRejected();
    } catch {
      toast.error('Kunne ikke slette');
    }
  };

  if (loading) return <div className="h-32 bg-muted animate-pulse rounded-md" />;

  if (items.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-12 text-center text-muted-foreground" data-testid="rejected-empty">
          <ChatCircleDots size={32} weight="thin" className="mx-auto mb-2" />
          <p>Ingen avviste forslag akkurat nå.</p>
          <p className="text-xs mt-1">
            Når du trykker «Avvis» i godkjenningskøen, lander forslaget her.
            Hvis et nytt forslag for samme produkt blir godkjent, fjernes det automatisk herfra.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="rejected-queue">
      <p className="text-sm text-muted-foreground">
        {items.length} avviste forslag. Re-prøv hvis du ombestemmer deg, eller slett for å glemme.
      </p>
      {items.map((it) => {
        const kindMeta = KIND_LABEL[it.kind] || { label: it.kind, color: 'bg-muted' };
        const fields = Object.keys(it.changes || {});
        return (
          <Card key={it.approval_id} className="border" data-testid={`rejected-${it.approval_id}`}>
            <CardContent className="p-4 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-[10px] ${kindMeta.color}`}>{kindMeta.label}</Badge>
                  <span className="font-medium truncate">{it.product_name || '(uten navn)'}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Avvist {new Date(it.rejected_at).toLocaleString('nb-NO')} av {it.rejected_by || 'ukjent'}
                </p>
                {fields.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Endringer: {fields.join(', ')}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => retry(it.approval_id)}
                  className="gap-1"
                  data-testid={`retry-${it.approval_id}`}
                >
                  <ArrowsClockwise size={14} /> Re-prøv
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(it.approval_id)}
                  className="gap-1 text-destructive hover:text-destructive"
                  data-testid={`delete-${it.approval_id}`}
                >
                  <Trash size={14} /> Slett
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
